import React, { useState } from 'react';
import { Button, message, Typography, Space, Avatar, Popconfirm } from 'antd';
import { PageContainer, ProTable } from '@ant-design/pro-components';
import type { ProColumns } from '@ant-design/pro-components';
import axiosApiInstance from '../../../api/axiosClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

type YouTubeChannelAuth = {
  channel_id: string;
  channel_title: string;
  thumbnail_url: string;
  created_at: string;
  updated_at: string;
};

const YouTubeAuthorizations: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch authorized channels
  const { data: authorizedChannels, isLoading, refetch } = useQuery({
    queryKey: ['authorizedChannels'],
    queryFn: async () => {
      const response = await axiosApiInstance.get('/api/youtubereports/auth/channels');
      return response.data?.channels || [];
    }
  });

  // Delete channel authorization mutation
  const deleteChannelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      await axiosApiInstance.delete(`/api/youtubereports/auth/channels/${channelId}`);
      return channelId;
    },
    onSuccess: () => {
      message.success('Channel authorization removed successfully');
      queryClient.invalidateQueries({ queryKey: ['authorizedChannels'] });
    },
    onError: (error) => {
      console.error('Error removing authorization:', error);
      message.error('Failed to remove channel authorization');
    }
  });

  // Handle authorization of a new channel
  const handleAuthorize = async () => {
    setLoading(true);
    try {
      const response = await axiosApiInstance.get('/api/youtubereports/auth/url');
      if (response.data && response.data.auth_url) {
        // Open the authorization URL in a new window
        window.open(response.data.auth_url, '_blank');
        message.info('Please complete authorization in the opened window. The table will update after successful authorization.');
      }
    } catch (error) {
      console.error('Error getting auth URL:', error);
      message.error('Failed to generate authorization URL');
    } finally {
      setLoading(false);
    }
  };

  const columns: ProColumns<YouTubeChannelAuth>[] = [
    {
      title: 'Channel',
      dataIndex: 'channel_title',
      render: (_, record) => (
        <Space>
          <Avatar src={record.thumbnail_url} />
          <Typography.Text>{record.channel_title}</Typography.Text>
        </Space>
      ),
      ellipsis: true,
    },
    {
      title: 'Channel ID',
      dataIndex: 'channel_id',
      copyable: true,
      ellipsis: true,
    },
    {
      title: 'Authorized On',
      dataIndex: 'created_at',
      render: (text) => dayjs(text as string).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      render: (text) => dayjs(text as string).fromNow(),
      sorter: (a, b) => dayjs(a.updated_at).unix() - dayjs(b.updated_at).unix(),
    },
    {
      title: 'Actions',
      key: 'action',
      valueType: 'option',
      render: (_, record) => [
        <Popconfirm
          key="delete"
          title="Remove authorization"
          // description="Are you sure you want to remove this channel authorization?"
          onConfirm={() => deleteChannelMutation.mutate(record.channel_id)}
          okText="Yes"
          cancelText="No"
        >
          <Button 
            danger 
            icon={<DeleteOutlined />}
            // loading={deleteChannelMutation.isPending}
          >
            Remove
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <PageContainer 
      title="YouTube Channel Authorizations" 
      subTitle="Manage YouTube channel authorizations for non-CMS channels"
    >
      <ProTable<YouTubeChannelAuth>
        columns={columns}
        dataSource={authorizedChannels}
        rowKey="channel_id"
        search={false}
        dateFormatter="string"
        loading={isLoading}
        options={{
          density: true,
          fullScreen: true,
          reload: () => refetch(),
        }}
        toolBarRender={() => [
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
          >
            Refresh List
          </Button>,
          <Button
            key="authorize"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAuthorize}
            loading={loading}
          >
            Authorize Channel
          </Button>,
        ]}
      />
    </PageContainer>
  );
};

export default YouTubeAuthorizations;